import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, FormControl, InputLabel, MenuItem, Select, Autocomplete, Box, Stack, Typography, FormHelperText } from "@mui/material";
import { IUser } from "models/IUser";
import { IOrganization } from "models/IOrganization";
import { useFormik } from 'formik';
import * as yup from 'yup';
import { DeviceBrandId } from "models/ISensor";
import { useTranslation } from "react-i18next";
import { getDeviceBrandName } from "helpers/ExternalDeviceHelper";

export type IAddExternalDeviceDialogProps = {
    openDialog: boolean;
    organizations: IOrganization[];
    users: IUser[];
    handleClose: () => void;
    handleAddExternalDevice: (id: string, name: string, brandId: string, organizationId: number, assignedUserId: string | null, meta: string | null) => void;
};

export default function AddExternalDeviceDialog({ openDialog, organizations, users, handleClose, handleAddExternalDevice }: IAddExternalDeviceDialogProps) {
    const { t } = useTranslation();

    const handleAddDialogClose: (event: any, reason: any) => void = (event, reason) => {
        if (reason && reason === "backdropClick") {
            return;
        }

        handleClose();
    };

    const validationSchema = yup.object({
        id: yup.string().required(t('field_required', 'This field is required.')!!),
        name: yup.string().required(t('field_required', 'This field is required.')!!),
        brandId: yup.string().required(t('field_required', 'This field is required.')!!),
        organization: yup.object().required(t('field_required', 'This field is required.')!!)
    });

    const formik = useFormik({
        initialValues: {
            id: '',
            name: '',
            brandId: '',
            organization: null as IOrganization | null,
            user: null as IUser | null,
            meta: ''
        },
        validationSchema,
        onSubmit: (values) => {
            handleAddExternalDevice(
                values.id,
                values.name,
                values.brandId,
                values.organization!!.id!!, 
                values.user == null ? null : values.user.id!!,
                values.meta == '' ? null : values.meta
            )
        }
    });

    return (
        <Dialog
            open={openDialog}
            onClose={handleAddDialogClose}
        >
            <form noValidate onSubmit={formik.handleSubmit}>
                <DialogTitle>{t('add_device', 'Add device')}</DialogTitle>
                <DialogContent>
                    <TextField
                        id="id"
                        name="id"
                        autoFocus
                        margin="normal"
                        label={t('id', 'Id')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formik.values.id}
                        onChange={formik.handleChange}
                        error={formik.touched.id && Boolean(formik.errors.id)}
                        helperText={formik.touched.id && formik.errors.id}
                        required
                    />

                    <TextField
                        id="name"
                        name="name"
                        margin="normal"
                        label={t('name', 'Name')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        required
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />

                    <FormControl margin="normal" fullWidth>
                        <InputLabel 
                            id="brandId-select-label"
                            error={formik.touched.brandId && Boolean(formik.errors.brandId)}
                            required>
                                {t('brand', 'Brand')}
                        </InputLabel>
                        <Select
                            id="brandId"
                            name="brandId"
                            labelId="brandId-select-label"
                            label={t('brand', 'Brand')}
                            value={formik.values.brandId}
                            onChange={formik.handleChange}
                        >
                                <MenuItem value={DeviceBrandId.Fitbit}>{getDeviceBrandName(DeviceBrandId.Fitbit)}</MenuItem>
                                <MenuItem value={DeviceBrandId.Flow}>{getDeviceBrandName(DeviceBrandId.Flow)}</MenuItem>
                                <MenuItem value={DeviceBrandId.CapMedic}>{getDeviceBrandName(DeviceBrandId.CapMedic)}</MenuItem>
                                <MenuItem value={DeviceBrandId.WoePal}>{getDeviceBrandName(DeviceBrandId.WoePal)}</MenuItem>
                                <MenuItem value={DeviceBrandId.SakuraTech}>{getDeviceBrandName(DeviceBrandId.SakuraTech)}</MenuItem>
                                <MenuItem value={DeviceBrandId.inBiot}>{getDeviceBrandName(DeviceBrandId.inBiot)}</MenuItem>
                                <MenuItem value={DeviceBrandId.AwarenessApp}>{getDeviceBrandName(DeviceBrandId.AwarenessApp)}</MenuItem>
                                <MenuItem value={DeviceBrandId.DSS2}>{getDeviceBrandName(DeviceBrandId.DSS2)}</MenuItem>
                                <MenuItem value={DeviceBrandId.CEMIC}>{getDeviceBrandName(DeviceBrandId.CEMIC)}</MenuItem>
                                <MenuItem value={DeviceBrandId.PolitoAndSAT}>{getDeviceBrandName(DeviceBrandId.PolitoAndSAT)}</MenuItem>
                                <MenuItem value={DeviceBrandId.Sykno}>{getDeviceBrandName(DeviceBrandId.Sykno)}</MenuItem>
                                <MenuItem value={DeviceBrandId.Anura}>{getDeviceBrandName(DeviceBrandId.Anura)}</MenuItem>
                                <MenuItem value={DeviceBrandId.URVMINOS}>{getDeviceBrandName(DeviceBrandId.URVMINOS)}</MenuItem>
                                <MenuItem value={DeviceBrandId.enControl}>{getDeviceBrandName(DeviceBrandId.enControl)}</MenuItem>
                        </Select>
                        {formik.touched.brandId && formik.errors.brandId && (
                            <FormHelperText error id="standard-weight-helper-text-email-login">
                                {' '}
                                {formik.errors.brandId}{' '}
                            </FormHelperText>
                        )}
                    </FormControl>
                    
                    <FormControl margin="normal" fullWidth>
                        <Autocomplete
                            fullWidth
                            id="organization-autocomplete-label"
                            options={organizations!!}
                            value={formik.values.organization}
                            onChange={(event, organization) => formik.setFieldValue('organization', organization)}
                            getOptionLabel={(organization) => {
                                if (organizations) {
                                    const exist = organizations.includes(organization)
                                    if (exist)
                                        return organization.name!!
                                }
                                return ''
                            }}
                            renderInput={(params) => 
                                <TextField
                                    {...params}
                                    name="organization"
                                    error={formik.touched.organization && Boolean(formik.errors.organization)}
                                    helperText={(formik.touched.organization && formik.errors.organization) ? <>{formik.errors.organization}</> : null}
                                    label={t('organization', 'Organization')}
                                    required
                                />
                            }
                            renderOption={(props, organization) => (
                                <Box component="li" {...props}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Stack spacing={0}>
                                            <Typography>{organization?.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {organization?.id}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}
                        />
                    </FormControl>

                    <FormControl margin="normal" fullWidth>
                        <Autocomplete
                            fullWidth
                            id="assignedUser-autocomplete-label"
                            options={users!!}
                            value={formik.values.user}
                            onChange={(event, user) => formik.setFieldValue('user', user)}
                            getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
                            renderInput={(params) => 
                                <TextField {...params} label={t('assigned_user', 'Assigned user')}/>
                            }
                            renderOption={(props, user) => (
                                <Box component="li" {...props}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Stack spacing={0}>
                                            <Typography>{user?.firstName} {user?.lastName}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {user?.id}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}
                        />
                    </FormControl>
                    
                    <TextField
                        id="meta"
                        name="meta"
                        margin="normal"
                        label={t('meta', 'Meta')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formik.values.meta}
                        onChange={formik.handleChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleAddDialogClose(null, null)} variant="outlined" color="secondary">{t('cancel', 'Cancel')}</Button>
                    <Button type="submit" variant="contained">{t('save', 'Save')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}